import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../../../Components/Common/navbar";
import { getUserOrdersByOrderId } from "../../../api/services/package.service";
import MobileViewContainer from "../../../Components/MobileViewContainer";
import { useAuthContext } from "../../../Components/AuthContext";
import { ReservationRulesGate, RESERVATION_RULES_STORAGE_VALUE } from "../../../Components/ReservationRulesNotice";
import { buildSameDayReservationRulesDismissKey, formatOrderDoneDate, getOrderDoneResults, getReservationRules } from "../orderDoneDisplay.mjs";

const OrderDone = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { userInfo } = useAuthContext();
  const [orders, setOrders] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasError, setHasError] = React.useState(false);
  const [hasConfirmedRules, setHasConfirmedRules] = React.useState(false);

  React.useEffect(() => {
    const fetchUserOrder = async () => {
      if (!orderId) {
        setOrders([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setHasError(false);
      try {
        const response = await getUserOrdersByOrderId(orderId);
        setOrders(getOrderDoneResults(response.data));
      } catch (error) {
        console.error(error);
        setHasError(true);
        setOrders([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserOrder();
  }, [orderId]);

  React.useEffect(() => {
    setHasConfirmedRules(false);
  }, [orderId]);

  const orderRules = React.useMemo(() => getReservationRules(orders), [orders]);
  const confirmRules = () => {
    const sameDayDismissKey = buildSameDayReservationRulesDismissKey(userInfo?.id, orders);
    if (sameDayDismissKey) {
      localStorage.setItem(sameDayDismissKey, RESERVATION_RULES_STORAGE_VALUE);
    }
    setHasConfirmedRules(true);
  };

  if (!isLoading && !hasError && orderRules.length > 0 && !hasConfirmedRules) {
    return (
      <MobileViewContainer>
        <Navbar hideHeader />
        <ReservationRulesGate rules={orderRules} onConfirm={confirmRules} />
      </MobileViewContainer>
    );
  }

  const renderStatusMessage = (message) => (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-10 text-center text-sm font-semibold text-gray-500 shadow-[0_6px_18px_rgba(0,0,0,0.04)]">
      {message}
    </div>
  );

  const formatReceiptDate = (value) => {
    const formatted = formatOrderDoneDate(value);
    return formatted === "-" ? formatted : formatted.replace(/-/g, ".");
  };

  const renderOrders = () => {
    if (isLoading) return renderStatusMessage("주문 내역을 불러오는 중입니다.");
    if (hasError) return renderStatusMessage("주문 내역을 불러오지 못했습니다.");
    if (!orders.length) return renderStatusMessage("주문 내역이 없습니다.");

    return (
      <div className="flex flex-col gap-4">
        {orders.map((order, key) => (
          <div key={order.id ?? key} className="relative rounded-t-2xl bg-white shadow-[0_2px_20px_rgba(0,0,0,0.04)]">
            <div className="p-5 pb-6">
              {key === 0 ? (
                <div className="mb-4 flex flex-col items-center text-center">
                  <div className="mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-green-50">
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <p className="m-0 font-semibold text-green-600">예약이 완료되었습니다</p>
                  {order.orderDate ? (
                    <p className="m-0 mt-1 text-sm text-gray-600">{formatReceiptDate(order.orderDate)} 결제</p>
                  ) : null}
                </div>
              ) : null}

              <div className="flex flex-col items-center border-t-2 border-b-2 border-dashed border-gray-200 py-4">
                <span className="mb-1 text-xs text-gray-900">예약번호</span>
                <p className="m-0 text-3xl font-black tabular-nums text-gray-900" style={{ fontFamily: "Gmarket-B" }}>#{order.id}</p>
              </div>

              <div className="space-y-2 pt-4">
                <div className="flex items-start justify-between gap-3 text-sm">
                  <span className="shrink-0 text-gray-900">상품명</span>
                  <span className="text-right font-bold text-gray-900">{order.name}</span>
                </div>
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="shrink-0 text-gray-900">예약일</span>
                  <span className="text-right font-bold text-gray-900">{formatReceiptDate(order.reservationDate)}</span>
                </div>
                <div className="flex items-start justify-between gap-3 text-sm">
                  <span className="shrink-0 text-gray-900">예약석</span>
                  <span className="text-right font-bold text-gray-900">{order.spot || "-"}</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -left-2 top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-gray-100"></div>
              <div className="absolute -right-2 top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-gray-100"></div>
              <div className="mx-4 border-t-2 border-dashed border-gray-200"></div>
            </div>

            <div className="flex items-center justify-between px-5 pb-3 pt-5">
              <span className="text-base font-bold text-gray-700">총 결제 금액</span>
              <strong className="text-2xl font-black text-[#e16036] tabular-nums" style={{ fontFamily: "Gmarket-B" }}>
                {Number(order.amount || 0).toLocaleString()}원
              </strong>
            </div>

            <svg className="block w-full" height="10" viewBox="0 0 100 10" preserveAspectRatio="none" aria-hidden="true">
              <polygon points="0,10 0,0 5,10 10,0 15,10 20,0 25,10 30,0 35,10 40,0 45,10 50,0 55,10 60,0 65,10 70,0 75,10 80,0 85,10 90,0 95,10 100,0 100,10" fill="#f3f4f6" />
            </svg>
          </div>
        ))}

        <div className="flex flex-col items-center gap-2 px-4 pt-5 pb-3 text-center">
          <p className="m-0 text-base text-gray-900">예약 시간에 맞춰 방문해 주세요!</p>
          <p className="m-0 text-sm text-gray-900">예약 내역은 마이페이지에서 확인 가능합니다.</p>
          <p className="m-0 text-sm font-semibold text-[#e16036]">예약 당일, 이용수칙을 다시 안내해 드려요.</p>
        </div>
      </div>
    );
  };

  return (
    <MobileViewContainer>
      <Navbar hideHeader />

      <div className="sticky top-0 z-10 flex h-18 items-center justify-center border-b border-gray-100 bg-white px-5">
        <h1 className="m-0 text-xl font-bold" style={{ fontFamily: "Gmarket-B" }}>예약 완료</h1>
      </div>

      <div
        className="flex flex-1 flex-col bg-gray-100 px-4 pt-5"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 80px)" }}
      >
        {renderOrders()}

        <div className="mt-auto flex flex-col gap-2 rounded-2xl bg-white px-4 py-3 shadow-[0_8px_32px_rgba(0,0,0,0.12)]">
          <button
            type="button"
            onClick={() => navigate("/my-page")}
            className="w-full rounded-xl bg-[#e16036] px-4 py-3.5 text-base font-bold text-white shadow-[0_4px_14px_rgba(225,96,54,0.25)] transition-colors hover:bg-[#cc5530]"
          >
            예약 내역 확인하기
          </button>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="w-full rounded-xl border-2 border-[#e16036] bg-white px-4 py-3 text-sm font-bold text-[#e16036] transition-colors hover:bg-[#fff4ee]"
          >
            홈으로
          </button>
        </div>
      </div>
    </MobileViewContainer>
  );
};
export default OrderDone;
